import { type ComponentType } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import type { LucideProps } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * NavTabs — horizontal sub-navigation used inside detail pages (e.g. the
 * project workspace tabs). Each tab is a real router Link so deep-links
 * and back/forward work natively.
 *
 *   <NavTabs items={[
 *     { to: '/projects/$projectId', params: { projectId }, label: 'Overview' },
 *     { to: '/projects/$projectId/audits', params: { projectId }, label: 'Audits' },
 *   ]} />
 */
export interface NavTabItem {
  to: string;
  label: string;
  /** Optional icon. */
  icon?: ComponentType<LucideProps>;
  /** Optional count/badge text shown after the label. */
  badge?: string | number;
  /** Optional hash (#integrations etc) for tab-style routes. */
  hash?: string;
  /** Active when current pathname starts with this prefix. */
  match?: string;
  /** Forwarded params for typed routes. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params?: any;
}

export interface NavTabsProps {
  items: NavTabItem[];
  /** When true (default), the tab list scrolls horizontally on narrow viewports. */
  scrollable?: boolean;
  className?: string;
}

export function NavTabs({
  items,
  scrollable = true,
  className,
}: NavTabsProps): JSX.Element {
  const { pathname, hash } = useRouterState({ select: (s) => s.location });

  return (
    <nav
      aria-label="Section navigation"
      className={cn(
        'relative -mx-1 border-b border-border/60',
        scrollable && 'overflow-x-auto',
        className,
      )}
    >
      <ul className="flex min-w-max items-center gap-1 px-1">
        {items.map((item) => {
          const matchOn = item.match ?? item.to;
          const isPathActive =
            matchOn === '/'
              ? pathname === '/'
              : pathname === matchOn || pathname.startsWith(`${matchOn}/`);
          const isActive = item.hash
            ? hash === item.hash.replace(/^#/, '') && isPathActive
            : isPathActive && !hash;
          const Icon = item.icon;
          return (
            <li key={`${item.to}-${item.label}`} className="relative">
              <Link
                to={item.to}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                params={item.params as any}
                hash={item.hash}
                className={cn(
                  'relative flex items-center gap-2 whitespace-nowrap px-3 py-2.5 text-sm font-medium outline-none transition-colors',
                  isActive
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {Icon ? <Icon className="h-3.5 w-3.5" aria-hidden /> : null}
                <span>{item.label}</span>
                {item.badge !== undefined ? (
                  <span
                    className={cn(
                      'inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold tabular',
                      isActive
                        ? 'bg-primary/15 text-primary'
                        : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {item.badge}
                  </span>
                ) : null}
                {isActive ? (
                  <motion.span
                    layoutId="nav-tab-underline"
                    className="absolute inset-x-2 -bottom-px h-[2px] rounded-full bg-primary"
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                ) : null}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
